import { Button, type ButtonProps } from '@/components/common/button'
import { Input } from '@/components/common/input'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '@/components/common/popover'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { TokenImage } from '@/components/common/tokenImage'
import { ArrowIcon } from '@/components/icons/arrow'
import { CheckIcon } from '@/components/icons/check'
import { SearchIcon } from '@/components/icons/search'
import { getBorrowMarketCollateralAsset } from '@/utils/borrowMarkets/getBorrowMarketCollateralAsset'
import { isBorrowMarketCollateralPairSearchMatch } from '@/utils/borrowMarkets/isBorrowMarketCollateralPairSearchMatch'
import { cn } from '@/utils/cn'
import type { BorrowMarketAssetData, BorrowMarketDetail, BorrowMarketPair } from '@/utils/internalAppSdk'
import { type ChangeEvent, forwardRef, useCallback, useMemo, useState } from 'react'

interface Props {
  pairs: BorrowMarketPair[]
  market: BorrowMarketDetail
  selectedPair: BorrowMarketPair
  onSelect: (marketAssetId: string) => void
}

export const BorrowMarketCollateralSelect = ({ pairs, market, selectedPair, onSelect }: Props) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selectedCollateralAsset = getBorrowMarketCollateralAsset(market, selectedPair)
  const filteredPairs = useMemo(
    () => pairs.filter((pair) => isBorrowMarketCollateralPairSearchMatch({ market, pair, search })),
    [market, pairs, search]
  )
  const onSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])
  const onCollateralSelect = useCallback(
    (marketAssetId: string) => {
      onSelect(marketAssetId)
      setOpen(false)
    },
    [onSelect]
  )

  if (!selectedCollateralAsset) return null

  if (pairs.length <= 1) {
    return <CollateralSelectTrigger asset={selectedCollateralAsset} disabled hideArrow />
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <CollateralSelectTrigger asset={selectedCollateralAsset} />
      </PopoverTrigger>
      <PopoverContent align="end" className="bg-surface p-0">
        <div className="px-3 py-2">
          <div className="relative inline-flex w-full items-center">
            <SearchIcon size={16} className="pointer-events-none absolute left-3 select-none text-body-foreground" />
            <Input
              placeholder="Find collateral"
              onChange={onSearchChange}
              value={search}
              inputMode="text"
              className="w-full rounded-xl bg-page pl-9 ring-inset"
            />
          </div>
        </div>
        <ScrollArea className="flex max-h-44 min-h-10 flex-col overflow-y-auto border-t">
          <PopoverList>
            {filteredPairs.map((pair) => {
              const collateralAsset = getBorrowMarketCollateralAsset(market, pair)
              if (!collateralAsset) return null

              return (
                <PopoverListItem key={pair.collateralMarketAssetId}>
                  <Button
                    variant="default"
                    className={cn(
                      'flex h-10 w-full min-w-0 items-center justify-between gap-2 px-3',
                      'rounded-xl hocus:bg-border ring-inset',
                      selectedPair.collateralMarketAssetId === pair.collateralMarketAssetId &&
                        'font-bold text-heading-foreground'
                    )}
                    onClick={() => onCollateralSelect(pair.collateralMarketAssetId)}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <TokenImage symbol={collateralAsset.asset.symbol} className="shrink-0" />
                      <span className="truncate">{collateralAsset.asset.symbol}</span>
                    </span>
                    <CheckIcon
                      size={16}
                      className={cn(
                        'shrink-0 text-success opacity-0',
                        selectedPair.collateralMarketAssetId === pair.collateralMarketAssetId && 'opacity-100'
                      )}
                    />
                  </Button>
                </PopoverListItem>
              )
            })}
            {filteredPairs.length === 0 && (
              <PopoverListItem className="h-10 px-3 text-sm">No collateral found</PopoverListItem>
            )}
          </PopoverList>
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

interface CollateralSelectTriggerProps extends ButtonProps {
  asset: BorrowMarketAssetData
  hideArrow?: boolean
}

const CollateralSelectTrigger = forwardRef<HTMLButtonElement, CollateralSelectTriggerProps>(
  ({ asset, className, hideArrow = false, ...props }, ref) => (
    <Button
      ref={ref}
      variant="input"
      className={cn(
        'group flex h-10 w-48 min-w-0 items-center justify-start gap-1 rounded-xl bg-page px-3 text-right',
        'hocus:border-accent',
        'disabled:cursor-default disabled:border-border disabled:hocus:border-border disabled:bg-page disabled:hocus:bg-page disabled:hocus:text-body-foreground disabled:text-body-foreground',
        className
      )}
      {...props}
    >
      <span>Collateral</span>
      <div className="ml-auto flex max-w-full items-center gap-1 overflow-hidden">
        <TokenImage symbol={asset.asset.symbol} className="shrink-0" />
        <span className="truncate text-heading-foreground">{asset.asset.symbol}</span>
        {!hideArrow && (
          <span className="group-data-[state=open]:-scale-y-100 transition-transform">
            <ArrowIcon size={16} direction="down" />
          </span>
        )}
      </div>
    </Button>
  )
)
CollateralSelectTrigger.displayName = 'CollateralSelectTrigger'
